'use client';

import 'BomdisoftComponents/console/styles.css';
import type { ConsoleNavGroupOptions, ConsoleNavRow } from 'BomdisoftComponents/console/index';
import { entryKeys, type NavRow, navRows } from 'BomdisoftComponents/console/nesting';
import { PlaceholderRows } from 'BomdisoftComponents/console/placeholder-rows';
import { useRowEntry } from 'BomdisoftComponents/row-entry/index';
import { type ReactNode, useEffect, useMemo } from 'react';

export type RowMarks = { entered: boolean; onEntryEnd: () => void; twistyColumn: boolean };

export type NavGroupProps = {
  entries: ConsoleNavRow[];
  head: ReactNode;
  loadingTestId: string;
  open: boolean;
  openKeys: ReadonlySet<string> | 'all';
  options: ConsoleNavGroupOptions;
  renderRow: (row: NavRow<ConsoleNavRow>, marks: RowMarks) => ReactNode;
  testId: string;
  wide: boolean;
};

const NO_KEYS: readonly string[] = [];

export const NavGroup = ({ entries, head, loadingTestId, open, openKeys, options, renderRow, testId, wide }: NavGroupProps) => {
  const { footer, loadingLabel, notice, pulseArrivals, tree } = options;
  const loading = loadingLabel !== undefined;
  const pulsing = pulseArrivals === true && !loading ? entryKeys(entries).join('\n') : '';
  const keys = useMemo(() => (pulsing === '' ? NO_KEYS : pulsing.split('\n')), [pulsing]);
  const { entered, settle } = useRowEntry(keys);

  const rows = open && !loading ? navRows(entries, openKeys) : [];
  const shown = rows.map(row => row.entry.key).join('\n');

  useEffect(() => {
    const visible = new Set(shown.split('\n'));
    for (const key of entered) {
      if (!visible.has(String(key))) settle(key);
    }
  }, [entered, settle, shown]);

  const twistyColumn = tree === true || entries.some(entry => (entry.children ?? []).length > 0);

  return (
    <div className='bomdisoft-console-nav-group' data-testid={testId}>
      {head}
      {loading && wide ? <PlaceholderRows label={loadingLabel} testId={loadingTestId} twistyColumn={twistyColumn} /> : null}
      {!loading && wide && notice ? notice : null}
      {rows.map(row => renderRow(row, { entered: entered.has(row.entry.key), onEntryEnd: () => settle(row.entry.key), twistyColumn }))}
      {!loading && wide && footer ? footer : null}
    </div>
  );
};
